import { Avatar, Box, Checkbox, FormControlLabel, FormGroup, IconButton, Modal, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { DisplayName } from '../../../app/AppUtils';
import { resetEmployeeRegister, resetRegisterStatusses, setAllPeriodChecks, setCheckedPeriod, setEmployeeShowPast } from '../../register/registerSlice';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../../subcomponents/alertSlice';
import BackButton from '../../subcomponents/BackButton';
import ContentLoader from '../../subcomponents/ContentLoader';
import ErrorDisplay from '../../subcomponents/ErrorDisplay';
import FilterSidebar from '../../subcomponents/FilterSidebar';
import PageLayout from '../../subcomponents/PageLayout';
import SearchStudentRemarkCard from './SearchStudentRemarkCard';
import { fetchSearchStudent, resetRemarkStatus, resetSearchStudentHistory, setStudentRegisterFilter, setStudentRegisterPage, setStudentRegisterParentCheck, setStudentRegisterReasonMode } from './searchStudentSlice';
import { resetHistory } from '../../history/historySlice';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';

// Tabs
import SearchStudentAttendanceTab from './tabs/attendance/SearchStudentAttendanceTab';
import SearchStudentPerformanceTab from './tabs/register/SearchStudentPerformanceTab';
import SearchStudentRegisterTab from './tabs/register/SearchStudentRegisterTab';
import SearchStudentSummaryTab from './tabs/register/SearchStudentSummaryTab';
import SearchStudentHistoryTab from './tabs/SearchStudentHistoryTab';
import SearchStudentInfoTab from './tabs/SearchStudentInfoTab';
import SearchStudentProgramTab from './tabs/SearchStudentProgramTab';
import SearchStudentProgressTab from './tabs/SearchStudentProgressTab';
import SearchStudentScheduleTab from './tabs/SearchStudentScheduleTab';
import SearchStudentTimetableTab from './tabs/SearchStudentTimetableTab';

function CustomTabPanel({ children, value, index, ...other }) {

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            sx={{ display: value !== index ? 'none' : 'flex', flex: '1 1 auto' }}
        >
            {value === index && (
                <Box sx={{ display: 'flex', flex: '1 1 auto', p: 1, borderRadius: '0px 0px 8px 8px' }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function SearchStudentResultPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { searchStudent, searchStudentStatus, studentRegisterPage, studentRegisterReasonMode, studentRegisterParentCheck, deleteStudentRemarkStatus, putStudentRemarkStatus, postStudentRemarkStatus, studentRegisterFilter } = useSelector((state) => state.search.student);
    const { activeDepartment } = useSelector((state) => state.settings.theme);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { subscriptionsDelete } = useSelector((state) => state.subscription);
    const { user } = useSelector((state) => state.user);
    const { registerStatus, registerAllStatus, periods, subscriptionStatus, employeeShowPast } = useSelector((state) => state.register);
    const { aspNetUser } = useSelector((state) => state.settings.app);

    const [tabValue, setTabValue] = useState(0);
    const [registerTabEnabled, setRegisterTabEnabled] = useState(false);

    const [open, setOpen] = useState(false);

    const callbackModal = () => {
        setOpen(false);
    };

    useEffect(() => {
        dispatch(resetHistory());
        dispatch(resetSearchStudentHistory());
    }, []);

    // Set register enabled state
    useEffect(() => {
        setRegisterTabEnabled(siomSettings?.showSearchRegisterTab && aspNetUser?.role?.includes("Subscriptions"));
    }, [siomSettings, user]);

    const handleSearchStudentFetch = () => {
        var departmentId = (siomSettings.useMultipleDepartments && activeDepartment) ? activeDepartment[0]?.id : null

        if (!siomSettings.useMultipleDepartments) {
            dispatch(fetchSearchStudent({ studentId: id, employeeId: user?.userId, departmentId: null }));
        }

        if (siomSettings.useMultipleDepartments && activeDepartment) {
            dispatch(fetchSearchStudent({ studentId: id, employeeId: user?.userId, departmentId: departmentId }));
        }
    }

    // Get student data (progress & info)
    useEffect(() => {
        if (user !== null) {
            if (user?.userId !== null) {
                handleSearchStudentFetch();
                dispatch(resetRegisterStatusses());
            }
        }
    }, [activeDepartment, siomSettings, user, user?.userId]);

    // Refetch after (un)subscribing
    useEffect(() => {
        if (subscriptionsDelete.status === "success" || subscriptionStatus === "success") {
            handleSearchStudentFetch();
            dispatch(resetRegisterStatusses());
        }
    }, [subscriptionsDelete.status, subscriptionStatus]);

    // Post remark callback
    useEffect(() => {
        if (postStudentRemarkStatus === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetRemarkStatus());
            handleSearchStudentFetch();
        } else if (postStudentRemarkStatus === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetRemarkStatus());
        };
    }, [postStudentRemarkStatus]);

    // Update remark callback
    useEffect(() => {
        if (putStudentRemarkStatus === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetRemarkStatus());
            handleSearchStudentFetch();
        } else if (putStudentRemarkStatus === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetRemarkStatus());
        };
    }, [putStudentRemarkStatus]);

    // Delete remark callback
    useEffect(() => {
        if (deleteStudentRemarkStatus === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetRemarkStatus());
            handleSearchStudentFetch();
        } else if (deleteStudentRemarkStatus === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetRemarkStatus());
        };
    }, [deleteStudentRemarkStatus]);

    const tabsList = [
        {
            label: resourceFinal[appLanguage].words.PROGRESS,
            component: <SearchStudentProgressTab />,
            condition: true,
        },
        {
            label: resourceFinal[appLanguage].words.REGISTER,
            component:
                studentRegisterPage === 'REGISTER' ? <SearchStudentRegisterTab studentId={id} /> :
                    studentRegisterPage === 'PERFORMANCE' ? <SearchStudentPerformanceTab studentId={id} /> :
                        studentRegisterPage === 'SUMMARY' ? <SearchStudentSummaryTab studentId={id} /> :
                            <ErrorDisplay />,
            condition: registerTabEnabled,
        },
        {
            label: resourceFinal[appLanguage].words.SCHEDULE,
            component: <SearchStudentScheduleTab studentId={id} />,
            condition: true,
        },
        {
            label: resourceFinal[appLanguage].words.TIMETABLE,
            component: <SearchStudentTimetableTab studentId={id} />,
            condition: true,
        },
        {
            label: resourceFinal[appLanguage].words.ATTENDANCE,
            component: <SearchStudentAttendanceTab studentId={id} />,
            condition: true,
        },
        {
            label: resourceFinal[appLanguage].words.PROGRAM,
            component: <SearchStudentProgramTab studentId={id} />,
            condition: true,
        },
        {
            label: resourceFinal[appLanguage].words.STUDENT_DATA,
            component: <SearchStudentInfoTab />,
            condition: true,
        },
        {
            label: resourceFinal[appLanguage].words.HISTORY,
            component: <SearchStudentHistoryTab studentId={id} />,
            condition: true,
        },
    ]

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const FilterFragment = () => {
        return (
            <>
                <ToggleButtonGroup
                    color="primary"
                    disabled={registerStatus === "loading" || registerAllStatus === "loading"}
                    fullWidth
                    exclusive
                    value={studentRegisterReasonMode ? "EMPLOYEE" : "STUDENT"}
                    onChange={(value) => dispatch(setStudentRegisterReasonMode(value === "EMPLOYEE" ? true : false))}
                >
                    {siomSettings?.showSearchRegisterTab && <ToggleButton value="STUDENT">{resourceFinal[appLanguage].words.STUDENT}</ToggleButton>}
                    <ToggleButton value="EMPLOYEE">{resourceFinal[appLanguage].words.ROLE_EMPLOYEE}</ToggleButton>
                </ToggleButtonGroup>

                <FormControlLabel sx={{ alignSelf: 'center' }}
                    control={
                        <Checkbox
                            checked={employeeShowPast}
                            disabled={registerAllStatus === "loading"}
                            onChange={() => {
                                dispatch(setEmployeeShowPast(!employeeShowPast));
                                dispatch(resetEmployeeRegister());
                            }}
                        />
                    }
                    label={`${resourceFinal[appLanguage].messages.SHOW_PAST_EMPLOYEE_REGISTER}`}
                />

                {((registerStatus === "success" || registerAllStatus === "success") && (registerStatus !== "loading" && registerAllStatus !== "loading")) &&
                    <>
                        {periods?.length > 0 && (
                            <>
                                <FormControlLabel
                                    label={resourceFinal[appLanguage].words.PERIODS}
                                    control={
                                        <Checkbox
                                            checked={studentRegisterParentCheck}
                                            onChange={
                                                () => {
                                                    dispatch(setStudentRegisterParentCheck())
                                                    periods?.forEach((item) => {
                                                        dispatch(setAllPeriodChecks({ id: item.periodId, bool: !studentRegisterParentCheck }));
                                                    });
                                                }
                                            }
                                        />
                                    } />
                                <FormGroup sx={{ width: 1, alignSelf: 'center' }}>
                                    {periods?.map((item) => <PeriodCheckbox key={item.periodId} item={item} />)}
                                </FormGroup>
                            </>
                        )}
                    </>
                }
            </>
        );
    };

    const PeriodCheckbox = ({ item }) => {
        return (
            <FormControlLabel
                sx={{ alignSelf: 'left', ml: 2 }}
                label={item.name}
                control={
                    <Checkbox
                        checked={item.checked}
                        onChange={() => {
                            dispatch(setCheckedPeriod(item.periodId))
                        }}
                    />
                }
            />
        );
    };

    const handleRegisterBackClick = () => {
        if (studentRegisterPage === 'REGISTER')
            navigate(-1);
        else
            dispatch(setStudentRegisterPage("REGISTER"));
    };

    return (
        <PageLayout
            title={
                (searchStudentStatus === "success" && searchStudent?.accessAllowed === true) ?
                    `${DisplayName(searchStudent.studentData?.firstName, searchStudent.studentData?.middleName, searchStudent.studentData?.lastName)} (${searchStudent.studentData?.number} - ${searchStudent.studentData?.extraInfo?.class ?? ""})`
                    :
                    `${resourceFinal[appLanguage].words.STUDENT}`}
            content={
                <ContentLoader
                    successCondition={searchStudentStatus === "success" && searchStudent?.accessAllowed === true}
                    errorCondition={searchStudentStatus === "failed" || searchStudent?.accessAllowed === false}
                    customError={
                        <>
                            {searchStudentStatus === "failed" && <ErrorDisplay message={resourceFinal[appLanguage].messages.ERROR_MESSAGE} />}
                            {searchStudent?.accessAllowed === false && <ErrorDisplay message={resourceFinal[appLanguage].messages.ERROR_MESSAGE_NO_ACCESS} />}
                        </>
                    }
                    content={
                        <>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={tabValue} onChange={handleChange} centered>
                                    {tabsList
                                        .filter(tab => tab.condition)
                                        .map((tab, index) => (
                                            <Tab key={index} label={tab.label} {...a11yProps(index)} />
                                        ))}
                                </Tabs>
                            </Box>
                            {tabsList
                                .filter(tab => tab.condition)
                                .map((tab, index) => (
                                    <CustomTabPanel key={index} value={tabValue} index={index}>
                                        {tab.component}
                                    </CustomTabPanel>
                                ))}
                            <Modal open={open} onClose={callbackModal}>
                                <>
                                    <SearchStudentRemarkCard handleClose={callbackModal} studentId={id} addNew />
                                </>
                            </Modal>
                        </>
                    }
                />
            } contentRight={
                <>
                    {searchStudentStatus === "success" &&
                        <Tooltip title={resourceFinal[appLanguage].words.REMARK} placement="left" arrow>
                            <IconButton onClick={() => setOpen(true)}>
                                <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                    <EditIcon sx={{ color: 'white.main', fontSize: 40 }} />
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    }
                    <Box sx={{ borderTop: 1, mt: 0.5, pt: 0.5, borderColor: "lightgrey" }}>
                        {(tabValue !== 1 || !registerTabEnabled) && <BackButton desktop />}
                        {(tabValue === 1 && registerTabEnabled) &&
                            <Tooltip title={resourceFinal[appLanguage].words.BACK} placement="left" arrow>
                                <IconButton sx={{ alignSelf: 'flex-end' }} onClick={handleRegisterBackClick}>
                                    <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                        <ArrowBackIcon sx={{ color: 'white.main', fontSize: 48 }} />
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                        }
                    </Box>
                </>
            } contentLeft={
                (tabValue === 1 && registerTabEnabled) && <FilterSidebar enableSearch searchFilter={studentRegisterFilter} searchFilterSet={(value) => dispatch(setStudentRegisterFilter(value))} filters={<FilterFragment />} />
            }
            bottomBar
            disableHeaderBackground
            disableContentBackground
        />
    );
}